'use client'

import { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'

import { reportSentryError } from 'lib/sentry'
import { useCartStore } from 'modules/cart'

import { useCheckoutPaymentStore } from '../../payment/store/checkoutPaymentStore'
import { PaymentMethodsNames } from '../../payment/types'
import { redirectToPaymentLink } from '../../payment/utils'
import { useEditSaleOrderModals } from '../providers/EditSaleOrderModalsContext'
import { EditSaleOrderStatus, getChangesInStructuredFormat } from '../utils'

import { useEditSaleOrderActions } from './useEditSaleOrderActions'

interface useEditOrderProps {
  open?: boolean
}

export const useSubmitEditSaleOrder = ({ open }: useEditOrderProps) => {
  const [paymentLoading, setPaymentLoading] = useState(false)
  const [blikCode, setBlikCode] = useState('')
  const [blikCodeModalOpen, setBlikCodeModalOpen] = useState(false)
  const [editStatusObject, setEditStatusObject] =
    useState<EditSaleOrderStatus | null>(null)
  const { cart } = useCartStore()
  const paymentMethod = useCheckoutPaymentStore(state => state.paymentMethod)
  const { finishEditSaleOrder } = useEditSaleOrderActions()
  const { clearStates } = useEditSaleOrderModals()

  const router = useRouter()

  useEffect(() => {
    if (cart && open) {
      setEditStatusObject(getChangesInStructuredFormat(cart, paymentMethod))
    }
  }, [open, cart, paymentMethod])

  const handleCreateOrder = useCallback(async () => {
    if (
      !editStatusObject?.orderId ||
      !cart?.id ||
      !editStatusObject?.paymentMethod.current.id
    ) {
      return
    }

    setPaymentLoading(true)
    try {
      const response = await finishEditSaleOrder({
        saleOrderId: cart.saleOrderId ?? editStatusObject.orderId,
        cartId: cart.id,
        paymentMethodId: editStatusObject.paymentMethod.current.id,
        blikCode,
        cancelLoading: () => setPaymentLoading(false)
      })

      const order = response?.data?.data

      if (!order) throw new Error('No order data received')

      clearStates()
      setBlikCode('')

      if (!order.paymentLink) {
        router.push(`/podsumowanie/${order.id}`)

        return
      }

      redirectToPaymentLink(order.paymentLink)
    } catch (err: unknown) {
      const error = err as
        | {
            saleOrderId: string
            paymentMethodName: PaymentMethodsNames
          }
        | undefined

      if (
        typeof err === 'object' &&
        error?.saleOrderId &&
        error?.paymentMethodName === PaymentMethodsNames.Blik
      ) {
        router.push(`/podsumowanie/${error.saleOrderId}`)
      } else {
        reportSentryError(err, 'Error creating order')
      }
    } finally {
      setPaymentLoading(false)
    }
  }, [editStatusObject, cart, blikCode, router, finishEditSaleOrder, clearStates])

  const handleBlikCodeChange = useCallback((_: string, value: string) => {
    setBlikCode(value)
  }, [])

  const toggleBlikCodeModal = useCallback((value: boolean) => {
    setBlikCodeModalOpen(value)
  }, [])

  return {
    paymentLoading,
    blikCode,
    blikCodeModalOpen,
    editStatusObject,
    handleCreateOrder,
    handleBlikCodeChange,
    toggleBlikCodeModal
  }
}
